/**
 * Agent Configuration API
 * Manages agent configurations and settings
 */

import { BaseApiClient } from "./base";

// ========== Types ==========

export interface AgentManifest {
  type: string;
  name: string;
  description: string;
  features: string[];
}

export interface Agent {
  id: string;
  workspace_id: string;
  type: string;
  name: string;
  instructions: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateAgentRequest {
  type: string;
  name: string;
}

export interface UpdateAgentRequest {
  instructions?: string | null;
  is_active?: boolean;
}

export interface AgentChannel {
  id: string;
  name: string;
  platform: string;
}

export interface AddChannelToAgentRequest {
  channelId: string;
}

export interface AgentKnowledgeSource {
  id: string;
  name: string;
  type: string;
}

export interface AddKnowledgeSourceToAgentRequest {
  knowledgeSourceId: string;
}

// ========== API Client ==========

class AgentsApiClient extends BaseApiClient {
  /**
   * List all agent configurations for the workspace
   */
  async list(params?: { channelId?: string }): Promise<Agent[]> {
    const queryParams = new URLSearchParams();
    if (params?.channelId) {
      queryParams.append("channelId", params.channelId);
    }
    const queryString = queryParams.toString();
    return this.request<Agent[]>(`/agents${queryString ? `?${queryString}` : ""}`);
  }

  async getCatalog(): Promise<AgentManifest[]> {
    return this.request<AgentManifest[]>("/agents/catalog");
  }

  /**
   * Create a new agent configuration
   */
  async create(data: CreateAgentRequest): Promise<Agent> {
    return this.request<Agent>("/agents", {
      method: "POST",
      body: JSON.stringify(data)
    });
  }

  /**
   * Get a specific agent configuration by ID
   */
  async get(id: string): Promise<Agent> {
    return this.request<Agent>(`/agents/${id}`);
  }

  /**
   * Update an agent configuration
   */
  async update(id: string, data: UpdateAgentRequest): Promise<Agent> {
    return this.request<Agent>(`/agents/${id}`, {
      method: "PUT",
      body: JSON.stringify(data)
    });
  }

  /**
   * Delete an agent configuration
   */
  async delete(id: string): Promise<{ success: boolean; id: string }> {
    return this.request<{ success: boolean; id: string }>(`/agents/${id}`, {
      method: "DELETE"
    });
  }

  /**
   * List all channels linked to an agent
   */
  async listChannels(agentId: string): Promise<AgentChannel[]> {
    return this.request<AgentChannel[]>(`/agents/${agentId}/channels`);
  }

  /**
   * Link a channel to an agent
   */
  async addChannel(agentId: string, data: AddChannelToAgentRequest): Promise<AgentChannel> {
    return this.request<AgentChannel>(`/agents/${agentId}/channels`, {
      method: "POST",
      body: JSON.stringify(data)
    });
  }

  /**
   * Remove a channel from an agent
   */
  async removeChannel(
    agentId: string,
    channelId: string
  ): Promise<{ success: boolean; agentId: string }> {
    return this.request<{ success: boolean; agentId: string }>(
      `/agents/${agentId}/channels/${channelId}`,
      {
        method: "DELETE"
      }
    );
  }

  /**
   * List all knowledge sources linked to an agent
   */
  async listKnowledgeSources(agentId: string): Promise<AgentKnowledgeSource[]> {
    return this.request<AgentKnowledgeSource[]>(`/agents/${agentId}/knowledge`);
  }

  /**
   * Link a knowledge source to an agent
   */
  async addKnowledgeSource(
    agentId: string,
    data: AddKnowledgeSourceToAgentRequest
  ): Promise<AgentKnowledgeSource> {
    return this.request<AgentKnowledgeSource>(`/agents/${agentId}/knowledge`, {
      method: "POST",
      body: JSON.stringify(data)
    });
  }

  /**
   * Remove a knowledge source from an agent
   */
  async removeKnowledgeSource(
    agentId: string,
    knowledgeSourceId: string
  ): Promise<{ success: boolean; agentId: string }> {
    return this.request<{ success: boolean; agentId: string }>(
      `/agents/${agentId}/knowledge/${knowledgeSourceId}`,
      {
        method: "DELETE"
      }
    );
  }
}

export const agentsApi = new AgentsApiClient();
